import {
  Card,
  DatePicker,
  Form,
  FormProps,
  Input,
  message,
  Select,
  Spin,
  Typography,
} from 'antd';
import AvatarUpload from 'components/AvatarUpload';
import FileUploadInput from 'components/FileUploadInput';
import FormContainer from 'components/FormContainer';
import SelectComponent from 'components/Select';
import dayjs from 'dayjs';
import useAgronomistList from 'hooks/useAgronomistList';
import useBankNameList from 'hooks/useBankNameList';
import useDeleteFilesMutation from 'hooks/useDeleteFileMutation';
import useUploadDocument from 'hooks/useUploadDocument';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import useMutateVerifyOpenAI from 'screens/admin-review/hooks/useMutateVerifyOpenAI';
import {
  convertPaddyBuyerToForm,
  SCROLL_OPTIONS,
  TPaddyBuyerForm,
  validateNationalID,
  validatePhoneNumber,
} from 'screens/stakeholders/helper';
import useMutatePaddyBuyer from 'screens/stakeholders/hooks/mutations/useMutatePaddyBuyer';
import usePaddyBuyerById from 'screens/stakeholders/hooks/queries/usePaddyBuyerById';
import { selectToken, selectUserCountry } from 'store/auth/selectors';
import { formProps, SUPPORTED_COUNTRIES } from 'utils/constants';
import {
  getCountryPhoneCode,
  getRoleFromToken,
  getStakeholderTypeForCountry,
} from 'utils/helpers';
import AddressForm from '../../AddressForm';
import VerificationWarning from '../../VerificationWarning';
import { useStakeholderFileManagement } from 'screens/stakeholders/hooks/useStakeholderFileManagement';

const breadcrumbItems = (isEdit: boolean) => [
  {
    title: (
      <Link className="link" to="/stakeholders/paddy-buyer">
        Farmer Stakeholder Management
      </Link>
    ),
  },
  {
    title: isEdit ? 'Edit' : 'Add Individually',
  },
];

const PaddyBuyerForm = ({ edit: isEdit = false }: { edit?: boolean }) => {
  const { t } = useTranslation();
  // Form State
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const [form] = Form.useForm();

  const country = Form.useWatch(['address', 'country'], form);

  const {
    existingPicture,
    profilePicture,
    existingNationalIdPictures,
    nationalIdPictures,
    deletedIdPictures,
    uploadedFirstPageContract,
    existingContractPictures,
    firstPageContract,
    contractPictures,
    deletedContractPictures,
    setProfilePicture,
    setNationalIdPictures,
    setDeletedIdPictures,
    setFirstPageContract,
    setContractPictures,
    setDeletedContractPictures,
    validateFiles,
    initializeFiles,
  } = useStakeholderFileManagement(isEdit, country);

  const userCountry = useSelector(selectUserCountry);
  const role = getRoleFromToken(useSelector(selectToken));

  const isUserPM = role === 'PM';

  const isSrAgRole = role === 'Sr Agronomist';

  const navigate = useNavigate();
  const { id } = useParams();

  const countryOptions = useMemo(
    () =>
      isSrAgRole
        ? SUPPORTED_COUNTRIES.filter(
            (country) => country?.value === userCountry,
          )
        : SUPPORTED_COUNTRIES,
    [userCountry, isSrAgRole],
  );

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country, {
      role: 'sr',
      labelKey: 'email',
      valueKey: 'email',
    });
  const { bankNameOptions, isLoading: isLoadingBank } = useBankNameList();
  const { data: paddyBuyerById, isLoading: isLoadingFetchPB } =
    usePaddyBuyerById(id);

  const { mutateAsync: mutateUploadPicture, isPending: isUploadingProfile } =
    useUploadDocument({
      onSuccess: () => setProfilePicture(undefined),
      successMessage: 'Saved Profile Picture',
    });

  const {
    mutateAsync: mutateUploadNationalID,
    isPending: isUploadingNationalID,
  } = useUploadDocument({
    onSuccess: () => setNationalIdPictures([]),
  });

  const { mutateAsync: mutateUploadContract, isPending: isUploadingContract } =
    useUploadDocument({
      onSuccess: () => setContractPictures([]),
    });

  const { mutateAsync: mutateDeleteFiles } = useDeleteFilesMutation();

  const { mutateAsync: mutateVerifyOpenAI } = useMutateVerifyOpenAI({
    showVerificationNotification: true,
  });

  const { mutate: mutatePaddyBuyer, isPending: isLoading } =
    useMutatePaddyBuyer({
      onSuccess: async (data) => {
        if (profilePicture) {
          await mutateUploadPicture({
            file: profilePicture,
            objectClass: 'PaddyBuyer',
            objectId: data?.data?.id,
            tag: 'PROFILE',
          });
        }

        if (nationalIdPictures.length) {
          await uploadNationalIdPictures(data?.data?.id);
        }

        if (firstPageContract) {
          await mutateUploadContract({
            file: firstPageContract,
            objectClass: 'PaddyBuyer',
            objectId: data?.data?.id,
            tag: 'FIRST_PAGE_CONTRACT',
          });
        }

        if (contractPictures.length) {
          await uploadContractPictures(data?.data?.id);
        }

        mutateVerifyOpenAI({
          stakeholderId: data?.data?.id,
          stakeholderType: 'PADDY_BUYER',
          verificationTrigger: isEdit ? 'AUTOMATIC_UPDATE' : 'AUTOMATIC_CREATE',
        });

        navigate(routes.paddyBuyer);
      },
      onFinish: () => {
        message.destroy('submit-paddy-buyer');
      },
      method: isEdit ? 'PUT' : 'POST',
    });

  const uploadNationalIdPictures = async (id: string) => {
    for (const file of nationalIdPictures) {
      try {
        await mutateUploadNationalID({
          file: file,
          objectClass: 'PaddyBuyer',
          objectId: id,
          tag: 'NATIONAL_ID',
        });
      } catch {
        message.error('Failed to upload National ID');
      }
    }
  };

  const uploadContractPictures = async (id: string) => {
    for (const file of contractPictures) {
      try {
        await mutateUploadContract({
          file: file,
          objectClass: 'PaddyBuyer',
          objectId: id,
          tag: 'SIGNED_CONTRACT',
        });
      } catch {
        message.error('Failed to upload Signed Contracts');
      }
    }
  };

  const onFieldsChanges = () => {
    if (isEdit && !isLoadingData) {
      setIsTouched(true);
    }
  };

  const getFormattedData = (values: TPaddyBuyerForm) => {
    const { accountHolderName, accountNumber, paymentType } = {
      ...values.bankAccountInfo,
    };
    const bankAccountInfo =
      accountHolderName && accountNumber && paymentType?.id
        ? values.bankAccountInfo
        : undefined;

    const formatValues = {
      ...values,
      contractStartDate: values.contractStartDate?.toISOString(),
      nationalIdPictures: undefined,
      id: isEdit ? paddyBuyerById?.data?.id : undefined,
      bankAccountInfos: bankAccountInfo ? [bankAccountInfo] : undefined,
      bankAccountInfo: undefined,
      phoneNumber: getCountryPhoneCode(country) + values.phoneNumber,
      agronomist: values?.agronomist?.email
        ? { email: values.agronomist.email }
        : undefined,
    };
    return formatValues;
  };

  const onFinish: FormProps<TPaddyBuyerForm>['onFinish'] = async (values) => {
    const formatValues = getFormattedData(values);

    if (validateFiles()) {
      message.loading({
        key: 'submit-paddy-buyer',
        content: 'Uploading stakeholder data and files...',
        duration: 0,
      });
      if (isEdit) await handleDeleteFile();
      mutatePaddyBuyer(formatValues);
    }
  };

  const handleDeleteFile = async () => {
    const deletedExistingFileIds = [
      ...deletedIdPictures,
      ...deletedContractPictures,
    ];

    if (!deletedExistingFileIds.length) return;

    await mutateDeleteFiles(deletedExistingFileIds.map((file) => file.uid));
  };

  const handleSaveForLater = async () => {
    setIsDraft(true);

    const values = form.getFieldsValue();

    try {
      await form.validateFields([
        'name',
        'phoneNumber',
        ['address', 'country'],
      ]);

      onFinish(values);
    } catch (e) {
      const error = e as { errorFields: { name: string[] }[] };
      const firstErrorField = error?.errorFields?.[0]?.name;

      form.scrollToField(firstErrorField, SCROLL_OPTIONS);

      message.error(
        'Please fill out Country, Name and Phone Number fields to save for later.',
      );
    }
    setIsDraft(false);
  };

  const isUploading =
    isUploadingProfile || isUploadingNationalID || isUploadingContract;

  const isSubmitDisabled = isLoading || isUploading || isDraft;

  const isDraftDisabled = isLoading || isUploading;

  useEffect(() => {
    if (isEdit && !!paddyBuyerById?.data) {
      const convert = convertPaddyBuyerToForm(paddyBuyerById?.data);
      form.setFieldsValue(convert);
      initializeFiles(paddyBuyerById?.data, form);

      if (paddyBuyerById?.data?.contractStartDate) {
        form.setFieldValue(
          ['contractStartDate'],
          dayjs(paddyBuyerById?.data?.contractStartDate),
        );
      }

      setTimeout(() => setIsLoadingData(false), 1000);
    }
  }, [paddyBuyerById?.data]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldValue(['address', 'country'], userCountry);
    }
  }, [isEdit]);

  return (
    <>
      <Spin spinning={isEdit && isLoadingFetchPB} fullscreen />
      <FormContainer
        form={form}
        onFinish={onFinish}
        onFieldsChange={onFieldsChanges}
        title={t(isEdit ? 'common.edit' : 'common.add', {
          value: getStakeholderTypeForCountry(country, 'PB').toLowerCase(),
        })}
        breadcrumbItems={breadcrumbItems(isEdit)}
        cancelBtnProps={{ disabled: isLoading || isUploading || isDraft }}
        onCancel={() => navigate(routes.paddyBuyer)}
        submitBtnProps={{
          disabled: (isEdit && !isTouched) || isSubmitDisabled,
          loading: isSubmitDisabled && !isDraft,
        }}
        submitBtnText={
          isEdit
            ? 'Save Changes'
            : `Submit ${getStakeholderTypeForCountry(country, 'PB')?.toLowerCase()}`
        }
        draftBtnProps={{
          disabled: (isEdit && !isTouched) || isDraftDisabled,
          loading: isDraftDisabled && isDraft,
          onClick: handleSaveForLater,
        }}
      >
        <VerificationWarning data={paddyBuyerById?.data} type="PADDY_BUYER" />
        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Personal Data
          </Typography.Title>
          <Form.Item label="Profile Picture" {...formProps}>
            <AvatarUpload
              file={profilePicture}
              setFile={(file) => {
                setProfilePicture(file);
                setIsTouched(true);
              }}
              existingPicture={existingPicture}
            />
          </Form.Item>
          <Form.Item
            name={['address', 'country']}
            label="Country"
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <Select
              placeholder="Select country"
              showSearch
              disabled={isEdit || isSrAgRole}
              options={countryOptions}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <Input placeholder="Input name" />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            required
            hasFeedback
            dependencies={[['address', 'country']]}
            rules={[
              { required: true },
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
              {
                max: 14,
                min: 8,
                message: 'Phone number must be between 8-14 digits',
              },
              {
                validator: (_, value) =>
                  validatePhoneNumber(value, 'paddy-buyers', country),
              },
            ]}
            validateDebounce={300}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
            {...formProps}
          >
            <Input
              prefix={
                getCountryPhoneCode(country) &&
                `+${getCountryPhoneCode(country)}`
              }
              type="tel"
              placeholder=" Input phone number here"
            />
          </Form.Item>
          <Form.Item
            name="nationalIdNumber"
            label="National ID Number"
            required={country !== 'Vietnam'}
            hasFeedback
            dependencies={[['address', 'country']]}
            rules={[
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
              {
                max: country !== 'Vietnam' ? 16 : 12,
                min: country !== 'Vietnam' ? 16 : 12,
                message: `ID number must be ${
                  country !== 'Vietnam' ? 16 : 12
                } digits`,
              },
              {
                validator: (_, value) =>
                  validateNationalID(
                    value,
                    'paddy-buyers',
                    country,
                    paddyBuyerById?.data?.nationalIdNumber,
                  ),
              },
            ]}
            validateDebounce={300}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
            {...formProps}
          >
            <Input
              showCount
              maxLength={country !== 'Vietnam' ? 16 : 12}
              placeholder="Input National ID Number"
            />
          </Form.Item>

          <Form.Item
            label="National ID Picture"
            name="nationalIdPicture"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
            {...formProps}
          >
            <FileUploadInput
              multiple
              listType="picture"
              uploadShape="drag"
              defaultFileList={existingNationalIdPictures}
              disabled={isEdit && paddyBuyerById?.data?.status === 'active'}
              onFileChange={(files) => {
                setNationalIdPictures(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (
                  existingNationalIdPictures.find((f) => f.uid === file.uid)
                ) {
                  setDeletedIdPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </Form.Item>

          {isUserPM ? (
            <Form.Item
              name={['agronomist', 'email']}
              label="Senior Agronomist Email"
              {...formProps}
            >
              <SelectComponent
                placeholder="Select Email"
                showSearch
                options={agronomistFilterOptions}
                loading={isAgronomistLoading}
              />
            </Form.Item>
          ) : null}
        </Card>

        <AddressForm
          form={form}
          type="paddyBuyer"
          autoReset={!isEdit || !isLoadingData}
        />

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Bank Information
          </Typography.Title>
          <Form.Item
            name={['bankAccountInfo', 'paymentType', 'id']}
            label="Bank Name"
            {...formProps}
          >
            <SelectComponent
              allowClear
              showSearch
              placeholder="Select Bank Name"
              options={bankNameOptions}
              loading={isLoadingBank}
            />
          </Form.Item>
          <Form.Item
            name={['bankAccountInfo', 'accountNumber']}
            label="Account Number"
            rules={[
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
            ]}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
            {...formProps}
          >
            <Input placeholder="Input account number" />
          </Form.Item>
          <Form.Item
            name={['bankAccountInfo', 'accountHolderName']}
            label="Account Holder Name"
            normalize={(value) => value && value?.toUpperCase()}
            {...formProps}
          >
            <Input placeholder="Account Holder Name" />
          </Form.Item>
        </Card>

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            Upload Contracts
          </Typography.Title>

          <Form.Item
            label="First Page Contract"
            name="firstPageContract"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
            {...formProps}
          >
            <FileUploadInput
              listType="picture"
              defaultFileList={
                uploadedFirstPageContract ? [uploadedFirstPageContract] : []
              }
              uploadShape="drag"
              onFileChange={(files) => {
                setFirstPageContract(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (existingContractPictures.find((f) => f.uid === file.uid)) {
                  setDeletedContractPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label="Signed Contract"
            name="contractPicture"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
            {...formProps}
          >
            <FileUploadInput
              multiple
              listType="picture"
              defaultFileList={existingContractPictures}
              uploadShape="drag"
              onFileChange={(files) => {
                setContractPictures(files);
                setIsTouched(true);
              }}
              onDelete={(file) => {
                if (existingContractPictures.find((f) => f.uid === file.uid)) {
                  setDeletedContractPictures((prev) => [...prev, file]);
                  setIsTouched(true);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            key={paddyBuyerById?.data?.contractStartDate}
            name="contractStartDate"
            label="Effective From"
            required={country !== 'Vietnam'}
            rules={[
              {
                required: country !== 'Vietnam',
              },
            ]}
            validateFirst
            {...formProps}
          >
            <DatePicker
              onChange={(date) =>
                form.setFieldsValue({ contractStartDate: date })
              }
              defaultValue={
                paddyBuyerById?.data?.contractStartDate
                  ? dayjs(paddyBuyerById?.data?.contractStartDate)
                  : null
              }
              format="DD MMMM, YYYY"
            />
          </Form.Item>
        </Card>
      </FormContainer>
    </>
  );
};

export default PaddyBuyerForm;
